/**
 * Suggestion utilities.
 */
import { buildHandle } from '@/utils/pathwayUtils';

const STATE_TRAITS = ['liquid', 'gas', 'solid'];
const SCORE_EXACT_MATCH = 1;
const SCORE_GENERIC_MATCH = 2;
const SCORE_PARTIAL_MATCH = 3;
const SCORE_INVALID = Infinity;

const areStateTraitsIncompatible = (outputTraits, inputTraits) => {
  const outputState = outputTraits.find(trait => STATE_TRAITS.includes(trait));
  const inputState = inputTraits.find(trait => STATE_TRAITS.includes(trait));
  return outputState && inputState && outputState !== inputState;
};

const hasTraitOverlap = (traits1, traits2) => traits1.some(trait => traits2.includes(trait));

const areTraitsCompatible = (outputType, inputType) => {
  const outputIsSpecific = outputType.generic === false;
  const inputIsSpecific = inputType.generic === false;
  const outputTraits = outputType.traits || [];
  const inputTraits = inputType.traits || [];

  // Both specific types: must have the same name (not just trait overlap)
  if (outputIsSpecific && inputIsSpecific) {
    return outputType.name === inputType.name;
  }

  // Early exit: incompatible state traits (liquid vs gas, etc.)
  if (areStateTraitsIncompatible(outputTraits, inputTraits)) return false;

  // At least one is generic: check trait overlap
  // For generic types, traits define which species can connect
  return hasTraitOverlap(outputTraits, inputTraits);
};

/**
 * Calculates a connection score (lower is better).
 * Score 1 = exact match (same specific type name)
 * Score 2 = generic match (one generic, one specific, compatible traits)
 * Score 3 = partial match (both generic with compatible traits)
 * Infinity = incompatible (invalid connection)
 */
export const getConnectionScore = (outputType, inputType) => {
  if (!outputType || !inputType) return SCORE_INVALID;

  // Check compatibility first - if incompatible, return Infinity
  if (!areTraitsCompatible(outputType, inputType)) return SCORE_INVALID;

  const outputIsSpecific = outputType.generic === false;
  const inputIsSpecific = inputType.generic === false;

  if (outputIsSpecific && inputIsSpecific) {
    return SCORE_EXACT_MATCH;
  }

  // One generic, one specific: generic match
  if (outputIsSpecific || inputIsSpecific) {
    return SCORE_GENERIC_MATCH;
  }

  // Both generic: partial match
  return SCORE_PARTIAL_MATCH;
};

// Must match the handle format used by real edges so connected-port checks work
export const createConnectionKey = (nodeId, ioName, direction) => {
  return buildHandle(nodeId, direction, ioName);
};

export const createSuggestionKey = (fromNode, fromIO) => {
  return `${fromNode}_${fromIO}`;
};

/**
 * Finds potential connections between two nodes
 * Only checks output-to-input connections (never input-to-input or output-to-output)
 * @param {Object} sourceNode - Source node data
 * @param {string} sourceNodeId - Source node ID
 * @param {Object} targetNode - Target node data
 * @param {string} targetNodeId - Target node ID
 * @param {Set<string>} connectedSources - Set of connected source handles
 * @param {Set<string>} connectedTargets - Set of connected target handles
 * @param {Set<string>} existingSourceKeys - Set of existing suggestion source keys
 * @param {Set<string>} existingTargetKeys - Set of existing suggestion target keys
 * @returns {Object[]} - Array of valid suggestions
 */
export const findConnectionsBetweenNodes = (
  sourceNode,
  sourceNodeId,
  targetNode,
  targetNodeId,
  connectedSources,
  connectedTargets,
  existingSourceKeys,
  existingTargetKeys,
) => {
  const suggestions = [];
  const sourceData = sourceNode.data || sourceNode;
  const targetData = targetNode.data || targetNode;

  // Only check outputs from source node to inputs of target node
  // This ensures we never suggest input-to-input or output-to-output connections
  for (const output of sourceData.outputs || []) {
    const sourceHandle = createConnectionKey(sourceNodeId, output.name, 'output');
    const sourceKey = createSuggestionKey(sourceNodeId, output.name);

    if (connectedSources.has(sourceHandle) || existingSourceKeys.has(sourceKey)) {
      continue;
    }

    for (const input of targetData.inputs || []) {
      const targetHandle = createConnectionKey(targetNodeId, input.name, 'input');
      const targetKey = createSuggestionKey(targetNodeId, input.name);

      if (connectedTargets.has(targetHandle) || existingTargetKeys.has(targetKey)) {
        continue;
      }

      // Calculate score and filter out invalid connections
      const score = getConnectionScore(output.type, input.type);
      if (score < SCORE_INVALID) {
        suggestions.push({
          from_node: sourceNodeId,
          from_io: output.name,
          to_node: targetNodeId,
          to_io: input.name,
          score,
        });
      }
    }
  }

  return suggestions;
};

export const buildConnectedSets = edges => {
  const connectedSources = new Set(edges.map(edge => edge.sourceHandle).filter(Boolean));
  const connectedTargets = new Set(edges.map(edge => edge.targetHandle).filter(Boolean));
  return { connectedSources, connectedTargets };
};

export const buildSuggestionKeySets = existingSuggestions => {
  const existingSourceKeys = new Set(existingSuggestions.map(s => createSuggestionKey(s.from_node, s.from_io)));
  const existingTargetKeys = new Set(existingSuggestions.map(s => createSuggestionKey(s.to_node, s.to_io)));
  return { existingSourceKeys, existingTargetKeys };
};

const calculateDownstreamPriority = (fromNode, toNode, nodeIndex, isToNewNode = false) => {
  const fromX = fromNode.position?.x ?? 0;
  const toX = toNode.position?.x ?? 0;

  // Since layout is RIGHT, downstream = higher x value
  // Calculate x difference (positive = downstream flow, negative = upstream flow)
  const xDiff = toX - fromX;

  let positionPriority;
  if (isToNewNode) {
    // For connections TO newNode: prefer upstream sources (lower x)
    // Invert: upstream sources (negative xDiff) get higher priority
    positionPriority = Math.max(0, Math.min(100, 50 - xDiff * 0.1));
  } else {
    // For connections FROM newNode: prefer downstream targets (higher x)
    // Downstream targets (positive xDiff) get higher priority
    positionPriority = Math.max(0, Math.min(100, 50 + xDiff * 0.1));
  }

  // Add node index as tiebreaker (later nodes in array = higher priority, assuming they were added later)
  // Normalize to 0-10 range
  const orderPriority = nodeIndex * 0.1;

  return positionPriority + orderPriority;
};

export const calculateSuggestedConnections = (nodes, newNodeId, existingEdges, existingSuggestions) => {
  const newNode = nodes.find(node => node.id === newNodeId);
  if (!newNode || !newNode.data) {
    return [];
  }

  const { connectedSources, connectedTargets } = buildConnectedSets(existingEdges);
  const { existingSourceKeys, existingTargetKeys } = buildSuggestionKeySets(existingSuggestions);

  const suggestions = [];

  // Create a map of node IDs to their indices for order-based prioritization
  const nodeIndexMap = new Map();
  nodes.forEach((node, index) => {
    nodeIndexMap.set(node.id, index);
  });

  for (let i = 0; i < nodes.length; i++) {
    const otherNode = nodes[i];
    if (otherNode.id === newNodeId || !otherNode.data) {
      continue;
    }

    const nodeIndex = nodeIndexMap.get(otherNode.id) ?? i;

    // Check connections from other_node to new_node
    const toNewNode = findConnectionsBetweenNodes(
      otherNode,
      otherNode.id,
      newNode,
      newNodeId,
      connectedSources,
      connectedTargets,
      existingSourceKeys,
      existingTargetKeys,
    );
    // Add priority for upstream connections (otherNode -> newNode)
    // Prefer nodes that are upstream (lower x) to feed into newNode
    toNewNode.forEach(suggestion => {
      const priority = calculateDownstreamPriority(otherNode, newNode, nodeIndex, true);
      suggestion.downstreamPriority = priority;
    });
    suggestions.push(...toNewNode);

    // Check connections from new_node to other_node
    const fromNewNode = findConnectionsBetweenNodes(
      newNode,
      newNodeId,
      otherNode,
      otherNode.id,
      connectedSources,
      connectedTargets,
      existingSourceKeys,
      existingTargetKeys,
    );
    // Add priority for downstream connections (newNode -> otherNode)
    // Prefer nodes that are downstream (higher x) to receive from newNode
    fromNewNode.forEach(suggestion => {
      const priority = calculateDownstreamPriority(newNode, otherNode, nodeIndex, false);
      suggestion.downstreamPriority = priority;
    });
    suggestions.push(...fromNewNode);
  }

  // Sort by downstream priority first (higher is better), then by score (lower is better)
  // This prioritizes connections that flow downstream in the pathway
  return suggestions.sort((a, b) => {
    // First sort by downstream priority (higher is better)
    const priorityA = a.downstreamPriority ?? 0;
    const priorityB = b.downstreamPriority ?? 0;
    if (priorityA !== priorityB) {
      return priorityB - priorityA;
    }
    // Then sort by score (lower is better) as tiebreaker
    return a.score - b.score;
  });
};
